/**
 * MatrixBackground - Orbital Scan analysis animation
 * Rewritten February 2026: Replaced canvas-based matrix rain with
 * pure CSS/SVG "Orbital Scan" animation for a modern, premium look.
 *
 * Features:
 * - Concentric pulse rings emanating from center
 * - Rotating golden scan arc (SVG)
 * - Sport-specific floating data labels at low opacity
 * - Subtle grid overlay using CSS background-image
 * - Radial vignette for depth
 * - No canvas, no requestAnimationFrame -- pure CSS animations
 *
 * @module v2/analyzer/MatrixBackground
 */

import React, { useMemo } from 'react';

/**
 * Returns a trimmed list of sport-specific terms for floating labels.
 * @param {string} sport - 'basketball' | 'baseball' | 'football'
 * @returns {string[]}
 */
const getSportTerms = (sport) => {
  if (sport === 'baseball') {
    return [
      'SPIN', 'VELO', 'IVB', 'FB', 'SL', 'CB', 'CH',
      'ERA', 'WHIP', 'FIP', 'K', 'TUNNEL', 'TELL',
      'SEAM', 'AXIS', 'TILT', 'BREAK', 'GRIP', 'SLOT',
      'RELEASE',
    ];
  }
  if (sport === 'football') {
    return [
      'EPA', 'DVOA', 'CPOE', 'RPO', 'BLITZ', 'COVER',
      'ROUTE', 'SNAP', 'MOTION', 'ZONE', 'MAN', 'PRESS',
      'SACK', 'TFL', 'YDS', 'TD', 'QBR', 'aDOT',
      'READ', 'PLAY',
    ];
  }
  // basketball (default)
  return [
    'PTS', 'AST', 'REB', '3PT', 'eFG', 'TS%', 'ORTG',
    'DRTG', 'PER', 'USG', 'PPP', 'ARC', 'SPIN', 'SHOT',
    'LIFT', 'BASE', 'SET', 'SWISH', 'VORP', 'BLK',
  ];
};

/**
 * MatrixBackground Component - Orbital Scan effect during analysis
 *
 * @param {Object} props
 * @param {boolean} props.isActive - Whether animation is active
 * @param {string} [props.phase='analyzing'] - Current phase (reserved for future use)
 * @param {string} [props.theme='dark'] - Current theme: 'dark' | 'light'
 * @param {string} [props.sportCategory='basketball'] - Sport category
 */
export const MatrixBackground = React.memo(({
  isActive,
  phase = 'analyzing',
  theme = 'dark',
  sportCategory = 'basketball',
}) => {
  const terms = useMemo(() => getSportTerms(sportCategory), [sportCategory]);

  if (!isActive || theme === 'light') return null;

  return (
    <div className="fixed inset-0 z-10 pointer-events-none overflow-hidden">
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(232,184,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(232,184,0,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Pulse rings emanating from center */}
      {[0, 1, 2, 3].map((i) => (
        <div
          key={`ring-${i}`}
          className="absolute rounded-full"
          style={{
            top: '50%',
            left: '50%',
            width: 200,
            height: 200,
            border: '1px solid #E8B800',
            opacity: 0,
            animation: `slam-pulse-ring 4s ease-out ${i}s infinite`,
          }}
        />
      ))}

      {/* Rotating scan arc */}
      <div
        className="absolute"
        style={{
          top: '50%',
          left: '50%',
          width: 300,
          height: 300,
          animation: 'slam-rotate-scan 6s linear infinite',
        }}
      >
        <svg viewBox="0 0 300 300" className="w-full h-full">
          <defs>
            <linearGradient id="slam-scan-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#E8B800" stopOpacity="0" />
              <stop offset="50%" stopColor="#E8B800" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#E8B800" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M150,150 L150,0 A150,150 0 0,1 256,44 Z"
            fill="url(#slam-scan-grad)"
            opacity="0.15"
          />
        </svg>
      </div>

      {/* Floating sport-specific terms */}
      {terms.map((term, i) => (
        <span
          key={`term-${i}`}
          className="absolute text-[10px] font-mono whitespace-nowrap"
          style={{
            top: `${10 + ((i * 4.5) % 85)}%`,
            color: '#E8B800',
            opacity: 0,
            animation: `slam-float-label ${15 + (i % 5) * 3}s linear ${i * 1.2}s infinite`,
          }}
        >
          {term}
        </span>
      ))}

      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)',
        }}
      />

      {/* Keyframes */}
      <style>{`
        @keyframes slam-pulse-ring {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0.15;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }
        @keyframes slam-rotate-scan {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        @keyframes slam-float-label {
          0% {
            transform: translateX(-120px);
            opacity: 0;
          }
          5% {
            opacity: 0.1;
          }
          95% {
            opacity: 0.1;
          }
          100% {
            transform: translateX(calc(100vw + 120px));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
});

export default MatrixBackground;
